import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { wholesaleOrders } from '../../services/api';
import { ArrowLeft, Package, MapPin, CreditCard, Download, RotateCcw, Truck } from 'lucide-react';
import { generateInvoicePdf } from '../../utils/generateInvoicePdf';
import ReorderModal from '../../components/ReorderModal';

const fmt = (n) =>
  '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_BADGE = {
  pending:   'bg-warning text-dark',
  confirmed: 'bg-info text-dark',
  shipped:   'bg-primary',
  delivered: 'bg-success',
  invoiced:  'bg-secondary',
  paid:      'bg-dark',
  cancelled: 'bg-danger',
};

const PAYMENT_BADGE = {
  pending:    'bg-warning text-dark',
  authorized: 'bg-info text-dark',
  captured:   'bg-success',
  invoiced:   'bg-secondary',
  paid:       'bg-dark',
  failed:     'bg-danger',
};

export default function CustomerOrderDetail() {
  const { id } = useParams();
  const [order,          setOrder]          = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [dlLoading,      setDlLoading]      = useState(false);
  const [reorderOrderId, setReorderOrderId] = useState(null);
  const [toast,          setToast]          = useState('');

  const handleDownload = async () => {
    setDlLoading(true);
    try {
      const res = await wholesaleOrders.getInvoice(id);
      await generateInvoicePdf(res.data.data);
    } catch {
      // silently fail
    } finally {
      setDlLoading(false);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  useEffect(() => {
    wholesaleOrders.get(id)
      .then(res => setOrder(res.data.order))
      .catch(err => {
        if (err.response?.status === 404) setError('Order not found.');
        else setError('Failed to load order details.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">{error || 'Order not found.'}</div>
        <Link to="/wholesale/orders" className="btn btn-outline-secondary">
          <ArrowLeft size={14} className="me-1" /> Back to Orders
        </Link>
      </div>
    );
  }


  return (
    <div className="container-fluid py-4 px-4" style={{ maxWidth: 900 }}>
      {toast && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1100 }}>
          <div className="toast show align-items-center text-bg-success border-0">
            <div className="d-flex">
              <div className="toast-body">{toast}</div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToast('')} />
            </div>
          </div>
        </div>
      )}

      {/* Back link + action buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Link to="/wholesale/orders" className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1">
          <ArrowLeft size={14} /> Back to Orders
        </Link>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
            onClick={() => setReorderOrderId(parseInt(id, 10))}
          >
            <RotateCcw size={14} /> Reorder
          </button>
          {['invoiced', 'paid', 'delivered'].includes(order.orderStatus) && (
            <button
              className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1"
              onClick={handleDownload}
              disabled={dlLoading}
            >
              {dlLoading
                ? <span className="spinner-border spinner-border-sm" />
                : <Download size={14} />}
              {' '}Download Invoice PDF
            </button>
          )}
        </div>
      </div>

      {/* Order header */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="mb-0 fw-bold">
            {order.qbo_invoice_number
              ? <>Invoice #{order.qbo_invoice_number}</>
              : <>Order #{order.orderID}</>}
          </h5>
          <span className={`badge ${STATUS_BADGE[order.orderStatus] || 'bg-secondary'}`}>
            {order.orderStatus}
          </span>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-sm-4">
              <small className="text-muted d-block">Order Date</small>
              <span className="fw-semibold">
                {new Date(order.orderDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
            <div className="col-sm-4">
              <small className="text-muted d-block">Order Total</small>
              <span className="fw-bold fs-5">{fmt(order.totalAmount)}</span>
            </div>
            <div className="col-sm-4">
              <small className="text-muted d-block">Payment</small>
              <span className={`badge ${PAYMENT_BADGE[order.paymentStatus] || 'bg-secondary'}`}>
                {order.paymentStatus}
              </span>
              {order.invoiceDueDate && (
                <div className="small text-muted mt-1">
                  Due: {new Date(order.invoiceDueDate).toLocaleDateString('en-US')}
                </div>
              )}
            </div>
            {order.trackingNumber && (
              <div className="col-sm-4">
                <small className="text-muted d-block">Tracking #</small>
                <span className="fw-semibold">{order.trackingNumber}</span>
              </div>
            )}
            {order.shippedDate && (
              <div className="col-sm-4">
                <small className="text-muted d-block">Shipped</small>
                <span>{new Date(order.shippedDate).toLocaleDateString('en-US')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-light d-flex align-items-center gap-2">
          <Package size={16} />
          <span className="fw-semibold">Items Ordered</span>
        </div>
        <div className="table-responsive">
          <table className="table table-striped mb-0">
            <thead className="table-light">
              <tr>
                <th>Product</th>
                <th>Vintage</th>
                <th className="text-center">Qty</th>
                <th className="text-end">Unit Price</th>
                <th className="text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map(item => (
                <tr key={item.orderItemID}>
                  <td>
                    <div className="fw-semibold">{item.name}</div>
                    <small className="text-muted">{item.varietal} · {item.appellation}</small>
                  </td>
                  <td>{item.vintage || '—'}</td>
                  <td className="text-center">{item.quantity}</td>
                  <td className="text-end">{fmt(item.pricePerUnit)}</td>
                  <td className="text-end fw-semibold">{fmt(item.totalPrice)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="table-light">
              <tr>
                <td colSpan={4} className="text-end fw-bold">Order Total</td>
                <td className="text-end fw-bold">{fmt(order.totalAmount)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Fulfillment & Payment */}
      <div className="card shadow-sm">
        <div className="card-header bg-light fw-semibold">Fulfillment &amp; Payment</div>
        <div className="card-body">
          <div className="row g-4">
            {/* ── Fulfillment details ── */}
            <div className="col-md-6">
              {order.fulfillmentType === 'pickup' ? (
                <>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <MapPin size={15} className="text-muted" />
                    <span className="fw-semibold">Pickup Location</span>
                    {order.pickup_status === 'ready' && (
                      <span className="badge bg-success ms-1">Ready for Pickup</span>
                    )}
                    {order.pickup_status === 'picked_up' && (
                      <span className="badge bg-dark ms-1">Picked Up</span>
                    )}
                  </div>
                  {order.pickupLocName ? (
                    <div className="text-muted">
                      <div className="fw-semibold text-dark">{order.pickupLocName}</div>
                      {order.pickupLocAddress && (
                        <div>
                          {[
                            order.pickupLocAddress,
                            order.pickupLocAddress2,
                            order.pickupLocCity,
                            order.pickupLocState,
                            order.pickupLocZip,
                          ].filter(Boolean).join(', ')}
                        </div>
                      )}
                      {order.pickupLocInstructions && (
                        <div className="mt-1 fst-italic small">{order.pickupLocInstructions}</div>
                      )}
                      {order.pickupLocLeadDays > 0 && (
                        <div className="mt-1 small">
                          Estimated lead time: <strong>{order.pickupLocLeadDays} day{order.pickupLocLeadDays !== 1 ? 's' : ''}</strong>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted small">Location details not available</span>
                  )}
                </>
              ) : (
                <>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    {order.fulfillmentType === 'delivery'
                      ? <Truck size={15} className="text-muted" />
                      : <MapPin size={15} className="text-muted" />
                    }
                    <span className="fw-semibold">
                      {order.fulfillmentType === 'delivery' ? 'Delivery' : 'Ship To'}
                    </span>
                  </div>
                  {order.shippingAddress ? (
                    <address className="mb-0 text-muted">
                      {order.companyName}<br />
                      {order.shippingAddress}<br />
                      {order.shippingCity}, {order.shippingState} {order.shippingZip}
                    </address>
                  ) : (
                    <span className="text-muted">No address on file</span>
                  )}
                  {order.shipping_cost > 0 && (
                    <div className="mt-2 small">
                      <span className="text-muted">Shipping: </span>
                      <strong>{fmt(order.shipping_cost)}</strong>
                      {order.shipping_rule_description && (
                        <span className="text-muted ms-1">({order.shipping_rule_description})</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Payment details ── */}
            <div className="col-md-6">
              <div className="d-flex align-items-center gap-2 mb-2">
                <CreditCard size={15} className="text-muted" />
                <span className="fw-semibold">Payment Method</span>
              </div>
              {order.paymentMethod ? (
                <p className="mb-1 text-muted">
                  {order.paymentMethod.brand?.toUpperCase()} •••• {order.paymentMethod.last4}
                  <br />
                  <small>Expires {order.paymentMethod.exp_month}/{order.paymentMethod.exp_year}</small>
                </p>
              ) : (
                <p className="mb-1 text-muted">Invoice / Net Terms</p>
              )}
              <span className={`badge ${PAYMENT_BADGE[order.paymentStatus] || 'bg-secondary'}`}>
                {order.paymentStatus}
              </span>
            </div>
          </div>
        </div>
      </div>

      <ReorderModal
        orderId={reorderOrderId}
        onClose={() => setReorderOrderId(null)}
        onSuccess={(count) => showToast(`${count} item${count !== 1 ? 's' : ''} added to cart.`)}
      />
    </div>
  );
}
